import React, { useState } from 'react';
import { useProject } from '../../context/ProjectContext';

/**
 * MoveToProjectModal - Modal for moving a conversation to a different project (SAT-65)
 *
 * Props:
 * - isOpen: boolean - Whether the modal is visible
 * - onClose: function - Called when modal should close
 * - conversation: object - The conversation to move
 * - onMove: function(conversationId, projectId) - Called when move is confirmed
 */
export default function MoveToProjectModal({ isOpen, onClose, conversation, onMove }) {
  const { projects } = useProject();
  const [selectedProjectId, setSelectedProjectId] = useState(conversation?.project_id || null);
  const [isMoving, setIsMoving] = useState(false);

  if (!isOpen || !conversation) return null;

  const handleMove = async () => {
    if (selectedProjectId === conversation.project_id) {
      onClose();
      return;
    }

    setIsMoving(true);
    try {
      await onMove(conversation.id, selectedProjectId);
      onClose();
    } catch (error) {
      console.error('Error moving conversation:', error);
    } finally {
      setIsMoving(false);
    }
  };

  // Get project colors for visual distinction
  const getProjectColor = (color) => {
    const colors = {
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      purple: 'bg-purple-500',
      orange: 'bg-orange-500',
      pink: 'bg-pink-500',
      yellow: 'bg-yellow-500',
      red: 'bg-red-500',
      gray: 'bg-gray-500'
    };
    return colors[color] || 'bg-gray-500';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-xs"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Move to Project
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Move "{conversation.title || 'Untitled conversation'}" to:
          </p>

          {/* Project list */}
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {/* All Projects option (no project) */}
            <button
              onClick={() => setSelectedProjectId(null)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors ${
                selectedProjectId === null
                  ? 'border-decisiv-blue bg-decisiv-blue/10 dark:bg-decisiv-blue/20'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
              }`}
            >
              <div className="w-3 h-3 rounded-full bg-gray-400" />
              <span className={`flex-1 text-left text-sm ${
                selectedProjectId === null
                  ? 'text-decisiv-blue font-medium'
                  : 'text-gray-700 dark:text-gray-300'
              }`}>
                All Projects (No project)
              </span>
              {selectedProjectId === null && (
                <svg className="w-5 h-5 text-decisiv-blue" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </button>

            {/* Project options */}
            {projects.map((project) => (
              <button
                key={project.id}
                onClick={() => setSelectedProjectId(project.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors ${
                  selectedProjectId === project.id
                    ? 'border-decisiv-blue bg-decisiv-blue/10 dark:bg-decisiv-blue/20'
                    : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                <div className={`w-3 h-3 rounded-full ${getProjectColor(project.color)}`} />
                <span className={`flex-1 text-left text-sm truncate ${
                  selectedProjectId === project.id
                    ? 'text-decisiv-blue font-medium'
                    : 'text-gray-700 dark:text-gray-300'
                }`}>
                  {project.name}
                </span>
                {project.conversation_count > 0 && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {project.conversation_count} chats
                  </span>
                )}
                {selectedProjectId === project.id && (
                  <svg className="w-5 h-5 text-decisiv-blue" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            ))}

            {projects.length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                No projects available. Create a project first.
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700
              border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600
              transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleMove}
            disabled={isMoving || selectedProjectId === conversation.project_id}
            className="px-4 py-2 text-sm font-medium text-white bg-decisiv-blue rounded-lg
              hover:bg-decisiv-blue/90 disabled:opacity-50 disabled:cursor-not-allowed
              transition-colors flex items-center gap-2"
          >
            {isMoving ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Moving...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                </svg>
                Move
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
